/**
 * UI Styles
 * Centralized CSS styles for SDK components
 */

import { Z_INDEX, DIMENSIONS } from '../constants/sdk-constants';

/**
 * Color scheme for skeleton shimmer
 */
export interface ColorScheme {
  shimmerBase: string;
  shimmerLight: string;
}

const LIGHT_LOADER: ColorScheme = {
  shimmerBase: '#e8e8e8',
  shimmerLight: '#f3f3f3',
};

type Rgb = { r: number; g: number; b: number };

function parseCssColor(color: string): Rgb | null {
  const value = color.trim().toLowerCase();
  if (!value || value === 'transparent' || value === 'rgba(0, 0, 0, 0)') {
    return null;
  }

  const rgba = value.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/,
  );
  if (rgba) {
    const alpha = rgba[4] !== undefined ? Number(rgba[4]) : 1;
    if (alpha < 0.05) return null;
    return {
      r: Math.round(Number(rgba[1])),
      g: Math.round(Number(rgba[2])),
      b: Math.round(Number(rgba[3])),
    };
  }

  const hex = value.startsWith('#') ? value.slice(1) : value;
  if (/^[0-9a-f]{3}$/.test(hex)) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    };
  }
  if (/^[0-9a-f]{6}$/.test(hex)) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  return null;
}

function mixChannel(from: number, to: number, amount: number): number {
  return Math.round(from + (to - from) * amount);
}

function lightenRgb(rgb: Rgb, amount: number): Rgb {
  return {
    r: mixChannel(rgb.r, 255, amount),
    g: mixChannel(rgb.g, 255, amount),
    b: mixChannel(rgb.b, 255, amount),
  };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const toHex = (c: number) =>
    Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Resolve any CSS color (named, hsl, hex, …) to a normalized `rgb()`/`rgba()`
 * string via the browser. Returns null if the value is invalid or document
 * is unavailable.
 */
function normalizeCssColor(input: string): string | null {
  if (typeof document === 'undefined') return null;
  const el = document.createElement('span');
  el.style.color = input;
  if (!el.style.color) return null;
  el.style.display = 'none';
  document.body.appendChild(el);
  const resolved = getComputedStyle(el).color;
  el.remove();
  return resolved || null;
}

/**
 * Loader shimmer from `appearance.variables.loaderColor`.
 * Default: light grey pair. When set, uses that as the base and lightens
 * for the highlight stop. Accepts any CSS color (hex, rgb, named, hsl, …).
 */
export function getLoaderColorScheme(loaderColor?: string): ColorScheme {
  if (!loaderColor) return LIGHT_LOADER;
  const normalized = normalizeCssColor(loaderColor) ?? loaderColor;
  const rgb = parseCssColor(normalized);
  if (!rgb) return LIGHT_LOADER;
  return {
    shimmerBase: rgbToHex(rgb),
    shimmerLight: rgbToHex(lightenRgb(rgb, 0.35)),
  };
}

/**
 * Get shimmer gradient for skeleton loader
 */
export function getShimmerGradient(colors: ColorScheme): string {
  return `linear-gradient(90deg, ${colors.shimmerBase} 25%, ${colors.shimmerLight} 50%, ${colors.shimmerBase} 75%)`;
}

/**
 * Overlay styles for popup mode
 */
export function getOverlayStyles(): string {
  return `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${Z_INDEX.OVERLAY};
  `;
}

/**
 * Container styles for popup mode
 */
export function getContainerStyles(): string {
  return `
    position: relative;
    width: ${DIMENSIONS.CONTAINER_WIDTH_PERCENT};
    max-width: ${DIMENSIONS.CONTAINER_MAX_WIDTH};
    height: ${DIMENSIONS.CONTAINER_HEIGHT_PERCENT};
    max-height: ${DIMENSIONS.CONTAINER_MAX_HEIGHT};
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  `;
}

/**
 * Close button styles
 */
export function getCloseButtonStyles(): string {
  return `
    position: absolute;
    top: 10px;
    right: 10px;
    width: ${DIMENSIONS.BUTTON_SIZE}px;
    height: ${DIMENSIONS.BUTTON_SIZE}px;
    border: none;
    background: transparent;
    font-size: 24px;
    cursor: pointer;
    z-index: ${Z_INDEX.MODAL};
    color: #333;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
}

/**
 * Iframe styles for popup mode
 */
export function getPopupIframeStyles(): string {
  return `
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
  `;
}

/**
 * Iframe styles for inline mode
 */
export function getInlineIframeStyles(fillParent: boolean): string {
  const width = fillParent ? '100%' : DIMENSIONS.DEFAULT_IFRAME_WIDTH;
  const maxWidth = fillParent ? 'none' : '100%';
  
  return `
    width: ${width};
    max-width: ${maxWidth};
    height: ${DIMENSIONS.MIN_HEIGHT}px;
    min-height: ${DIMENSIONS.MIN_HEIGHT}px;
    border: none;
    display: block;
  `;
}

/**
 * Loader container styles
 */
export function getLoaderContainerStyles(): string {
  return `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: ${Z_INDEX.LOADER};
    padding: 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
  `;
}

/**
 * Skeleton form column (matches checkout form width / spacing)
 */
export function getSkeletonCardStyles(fillParent: boolean): string {
  const width = fillParent ? '100%' : DIMENSIONS.DEFAULT_IFRAME_WIDTH;

  return `
    width: ${width};
    max-width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-sizing: border-box;
    flex-shrink: 0;
  `;
}

/**
 * Skeleton element base styles
 */
export function getSkeletonBaseStyles(shimmerGradient: string): string {
  return `
    background: ${shimmerGradient};
    background-size: 200% 100%;
    animation: inflowpay-shimmer 1.5s infinite;
  `;
}

/**
 * Row of horizontal payment-method placeholders
 */
export function getSkeletonMethodRowStyles(): string {
  return `
    display: flex;
    align-items: stretch;
    gap: 8px;
    width: 100%;
  `;
}

/**
 * Generic shimmer bar / box
 */
export function getSkeletonBarStyles(
  shimmerGradient: string,
  options: {
    height?: number;
    width?: string;
    radius?: number;
    /** Share horizontal space equally (method tabs only). */
    grow?: boolean;
  } = {},
): string {
  const height = options.height ?? 40;
  const width = options.width ?? '100%';
  const radius = options.radius ?? 8;
  const grow = options.grow === true;

  return `
    width: ${width};
    height: ${height}px;
    border-radius: ${radius}px;
    ${grow ? 'flex: 1 1 0; min-width: 0;' : 'flex: 0 0 auto;'}
    box-sizing: border-box;
    ${getSkeletonBaseStyles(shimmerGradient)}
  `;
}

/**
 * Pay button skeleton
 */
export function getButtonSkeletonStyles(shimmerGradient: string): string {
  return `
    width: 100%;
    height: 44px;
    border-radius: 8px;
    margin-top: 6px;
    ${getSkeletonBaseStyles(shimmerGradient)}
  `;
}

/**
 * Shimmer animation keyframes
 */
export const SHIMMER_ANIMATION = `
  @keyframes inflowpay-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

/**
 * 3DS overlay styles
 */
export function get3DSOverlayStyles(): string {
  return `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${Z_INDEX.OVERLAY};
  `;
}

/**
 * 3DS modal styles
 */
export function get3DSModalStyles(): string {
  return `
    position: relative;
    width: ${DIMENSIONS.CONTAINER_WIDTH_PERCENT};
    max-width: ${DIMENSIONS.CONTAINER_MAX_WIDTH};
    height: ${DIMENSIONS.CONTAINER_HEIGHT_PERCENT};
    max-height: ${DIMENSIONS.CONTAINER_MAX_HEIGHT};
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
  `;
}

/**
 * 3DS header styles
 */
export function get3DSHeaderStyles(): string {
  return `
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 20px;
    border-bottom: 1px solid #e5e5e5;
  `;
}

/**
 * 3DS content styles
 */
export function get3DSContentStyles(): string {
  return `
    flex: 1;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
  `;
}

/**
 * 3DS iframe styles
 */
export function get3DSIframeStyles(): string {
  return `
    width: 100%;
    height: 100%;
    border: none;
  `;
}
